import { WikipediaLink } from "../../shared/WikipediaLink";
import type { VictimDTO } from "@shared/contracts";
import { formatDateYMD } from "../../../utils/date";
import { Card, Space, Image, Divider } from "antd";
import styled from "styled-components";

// Panel
const VictimPanel = styled(Card)`
  color: #eeeeee;
  border-radius: 8px;
  width: 200px;
  font-family: veterantypewriter !important;
  .ant-card-body {
    padding: 12px 16px;
  }
`;

const HeaderRow = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 8px;
`;

const HeaderLeft = styled.span`
  font-weight: 700;
  letter-spacing: 0.25px;
`;

const HeaderLinks = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

const DetailBtn = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  color: #aaaaaa;
  font-family: veterantypewriter !important;
  font-size: 11px;
  padding: 0;
  width: 100%;
  text-align: center;
  letter-spacing: 0.3px;
  &:hover {
    color: #eeeeee;
  }
`;

/* Picture section — match SerialKillerPicture:
   - Card with border-radius: 0
   - padding: 12px; padding-bottom: 32px;
   - 3:2 aspect ratio with cropping (object-fit: cover)
*/
const PictureCard = styled(Card)`
  max-width: 240px;
  width: 100%;
  border-radius: 0;
  background: white;
  border: none;
  .ant-card-body {
    padding: 12px;
    padding-bottom: 32px;
  }
`;

const PictureRow = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
`;

const PopupImage = styled(Image)`
  width: 100% !important;
  height: 100% !important;

  .ant-image-img {
    width: 100%;
    height: 100%;
    object-fit: cover; /* crop if it doesn't fit */
    display: block;
  }
`;

const InfoSpace = styled(Space)`
  margin-top: 12px;
  width: 100%;
  font-size: 12px;
`;

const ColorDivider = styled(Divider)<{ $color: string }>`
  margin: 8px 0;
  border-top: 2px solid ${(props) => props.$color};
  border-radius: 2px;
`;

const DetailRowFlex = styled.div`
  display: flex;
  justify-content: space-between; /* label at start, value at end */
  align-items: center;
  gap: 8px;
  width: 100%;
`;

const LabelText = styled.span`
  text-align: start;
  flex: 0 0 auto; /* don't grow, stay at content width */
`;

const ValueText = styled.span`
  text-align: end;
  flex: 1 1 auto; /* take remaining space, stick to the end */
`;

export function VictimPopupContent({
  v,
  color,
}: {
  v: VictimDTO;
  color: string;
}) {
  const name = v.name || "Unknown victim";
  const status = v.status ?? "—";
  const date = formatDateYMD(v.dateOfCrime, "—");
  const place = v.addressOfCrime ?? "—";
  const picture = v.pictureLink || null;
  const age = v.age ?? null;
  const gender = v.gender ?? null;
  const cause = v.causeOfDeath ?? null;
  const wiki = v.wikipediaLink || null;

  // const { url: spotifyUrl } = useSpotifySearchLink(name, v.status ?? null);

  const rows: Array<[string, string | number | null]> = [
    ["Status", status],
    ["Cause of death", cause],
    ["Date", date],
    ["Gender", gender],
    ["Age", age],
  ];

  return (
    <VictimPanel variant="borderless">
      <HeaderRow>
        <HeaderLeft>{name}</HeaderLeft>
        <HeaderLinks>
          {/* {wiki && !v.killerId && <WikipediaLink href={wiki} />} */}
          {/* {spotifyUrl && <SpotifyLink href={spotifyUrl} />} */}
        </HeaderLinks>
      </HeaderRow>

      <InfoSpace direction="vertical" size={8}>
        {rows
          .filter(([, v]) => v != null && v !== "")
          .map(([label, value], idx) => (
            <DetailRowFlex key={label + idx}>
              <LabelText>{label}</LabelText>
              <ValueText>{value}</ValueText>
            </DetailRowFlex>
          ))}
      </InfoSpace>

      <ColorDivider $color={color} />

      <DetailBtn data-action="victim-detail" type="button">
        View Details
      </DetailBtn>
    </VictimPanel>
  );
}
